import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { Hangar, Obstacle, ObstacleType } from "../../../types";
import { uuidv4 } from "../../../utils";
import { MetricAwareTextField } from "../../../widgets/MetricAwareTextField/MetricAwareTextField";
import { getCircleGeometry, Point } from "./obstacleUtils";

type Props = {
  open: boolean;
  center: Point | null;
  hangar: Hangar;
  onSave: (obstacle: Obstacle) => void;
  onClose: () => void;
};

export const CircleObstacleDialog: React.FC<Props> = ({
  open,
  center,
  hangar,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();

  const [type, setType] = React.useState<ObstacleType>(
    activeFBO?.subscription === "standard"
      ? ObstacleType.NO_TOW_AREA
      : ObstacleType.OBSTRUCTION
  );
  const [height, setHeight] = React.useState<number | null>(null);
  const [diameter, setDiameter] = React.useState<number>(2);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setType(
        activeFBO?.subscription === "standard"
          ? ObstacleType.NO_TOW_AREA
          : ObstacleType.OBSTRUCTION
      );
      setHeight(null);
      setDiameter(2);
    }
  }, [open, activeFBO?.subscription]);

  const handleSave = () => {
    if (!center || diameter <= 0) return;

    const radius = diameter / 2;
    const geom = getCircleGeometry(center, radius);

    const obstacle: Obstacle = {
      id: uuidv4(),
      fbo_id: activeFBO.id,
      hangar_id: hangar.id,
      geom,
      type,
      height,
      // Bounding box for circle
      x: center.x - radius,
      y: center.y - radius,
      width: diameter,
      depth: diameter,
    };

    onSave(obstacle);
  };

  const unit = activeFBO?.settings?.useMetricSystem ? "m" : "ft";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t(
          "settings.hangarSettings.obstacleToolbar.circleDialogTitle",
          "Add Circle Obstacle"
        )}
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
          {/* Circle info */}
          {center && (
            <Box
              sx={{
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {t(
                  "settings.hangarSettings.obstacleToolbar.circleInfo",
                  "Circle Info"
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  "settings.hangarSettings.obstacleToolbar.centerPosition",
                  "Center"
                )}
                : ({center.x.toFixed(1)}, {center.y.toFixed(1)}) {unit}
              </Typography>
            </Box>
          )}

          {/* Diameter input */}
          <MetricAwareTextField
            unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
            type="number"
            fullWidth
            required
            inputProps={{
              min: 0.5,
              max: 100,
              step: 0.5,
            }}
            size="small"
            onChange={(value) => setDiameter(value)}
            value={diameter}
            label={t(
              "settings.hangarSettings.obstacleToolbar.labels.diameter",
              { unit, defaultValue: `Diameter (${unit})` }
            )}
            autoFocus
          />

          {/* Type selector */}
          <TextField
            fullWidth
            size="small"
            select
            required
            label={t("settings.hangarSettings.obstacleToolbar.labels.type", "Type")}
            value={type}
            onChange={(evt) => setType(evt.target.value as ObstacleType)}
          >
            <MenuItem
              value={ObstacleType.OBSTRUCTION}
              disabled={activeFBO?.subscription === "standard"}
            >
              {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.obstruction")}
            </MenuItem>
            <MenuItem value={ObstacleType.NO_TOW_AREA}>
              {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.noTowArea")}
            </MenuItem>
            <MenuItem value={ObstacleType.UNOBSTRUCTIVE}>
              {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.unobstructive")}
            </MenuItem>
            <MenuItem value={ObstacleType.CAUTION}>
              {t("settings.hangarSettings.obstacleToolbar.obstacleTypes.caution")}
            </MenuItem>
          </TextField>

          {/* Height input */}
          <MetricAwareTextField
            unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
            type="number"
            fullWidth
            inputProps={{
              min: 1,
              max: 100,
              step: 0.1,
            }}
            size="small"
            onChange={(value) => setHeight(value)}
            value={height}
            label={t("settings.hangarSettings.obstacleToolbar.labels.height", {
              unit,
            })}
          />

          <Typography variant="caption" color="text.secondary">
            {t(
              "settings.hangarSettings.obstacleToolbar.heightHint",
              "Height is optional. Set it if the obstacle has height restrictions for aircraft placement."
            )}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t("settings.hangarSettings.obstacleToolbar.buttons.cancel", "Cancel")}
        </Button>
        <div style={{ flex: "1 0 0" }} />
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={!center || diameter <= 0}
        >
          {t("settings.hangarSettings.obstacleToolbar.buttons.add", "Add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
